import React from 'react';
import wingitDesign from '../designSystem';

export default function FlightSummaryCard({
  heading,
  nextBooking,
  nextBookingAircraft,
  bookingLabel,
  isMultiDay,
  planningStatusText,
  returnLegTargetBooking,
  returnLegStatus,
  returnLegStatusMeta,
  activeReturnLegBooking,
  hasFutureMultiDayBooking,
  returnLegSaving,
  returnLegNotice,
  returnLegError,
  planningComplete,
  onMakeBooking,
  onSaveReturnPending,
  onMarkReturnComplete,
}) {
  const ds = wingitDesign;
  const cleanStatus = cleanStatusText(
    planningStatusText
  );

  const aircraftRegistration = nextBooking
    ? nextBookingAircraft?.registration ||
      'Aircraft not linked'
    : 'No aircraft booked';

  const aircraftType =
    nextBookingAircraft?.icao_type || '';

  return (
    <section style={styles.card}>
      <div style={styles.topRow}>
        <div style={styles.headingBlock}>
          <div style={styles.kicker}>
            UPCOMING FLIGHT
          </div>

          <div style={styles.aircraftLine}>
            <span style={styles.registration}>
              {aircraftRegistration}
            </span>

            {aircraftType ? (
              <span style={styles.aircraftType}>
                {aircraftType}
              </span>
            ) : null}

            {isMultiDay ? (
              <span style={styles.badge}>
                MULTI-DAY
              </span>
            ) : null}
          </div>

          <div style={styles.bookingLine}>
            {nextBooking
              ? bookingLabel
              : 'No upcoming flights'}
          </div>
        </div>

        <StatusPill
          complete={planningComplete}
          text={cleanStatus}
        />
      </div>

      {!nextBooking ? (
        <button
          type="button"
          onClick={onMakeBooking}
          style={ds.buttons.primary}
        >
          Make a booking
        </button>
      ) : null}

      {returnLegTargetBooking ? (
        <div style={styles.returnLegRow}>
          <span style={styles.returnLegLabel}>
            Return leg
          </span>

          <span
            style={{
              ...styles.returnLegPill,
              border: `1px solid ${returnLegStatusMeta.border}`,
              background:
                returnLegStatusMeta.background,
              color: returnLegStatusMeta.fill,
            }}
          >
            {returnLegStatusMeta.label}
          </span>
        </div>
      ) : null}

      {activeReturnLegBooking &&
      returnLegStatus !== 'complete' ? (
        <div style={styles.warningBox}>
          Return-leg pre-flight required for this
          multi-day booking.
        </div>
      ) : hasFutureMultiDayBooking ? (
        <div style={styles.infoBox}>
          A separate pre-flight check will be needed
          before the return departure.
        </div>
      ) : null}

      {returnLegTargetBooking ? (
        <div style={styles.actions}>
          <button
            type="button"
            onClick={onSaveReturnPending}
            disabled={returnLegSaving}
            style={{
              ...ds.buttons.secondary,
              ...styles.actionButton,
              opacity: returnLegSaving ? 0.7 : 1,
            }}
          >
            Save pending
          </button>

          <button
            type="button"
            onClick={onMarkReturnComplete}
            disabled={
              returnLegSaving || !planningComplete
            }
            style={{
              ...ds.buttons.primary,
              ...styles.actionButton,
              opacity:
                returnLegSaving || !planningComplete
                  ? 0.65
                  : 1,
            }}
            title={
              planningComplete
                ? 'Mark return-leg pre-flight complete'
                : 'Complete the checklist first'
            }
          >
            Mark complete
          </button>

          {returnLegSaving ? (
            <span style={styles.saving}>
              Saving...
            </span>
          ) : null}

          {returnLegNotice ? (
            <span style={styles.success}>
              {returnLegNotice}
            </span>
          ) : null}

          {returnLegError ? (
            <span style={styles.error}>
              {returnLegError}
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function cleanStatusText(value) {
  return String(value || '')
    .replace('🟢', '')
    .replace('🟠', '')
    .replace('🟡', '')
    .trim();
}

function StatusPill({
  complete,
  text,
}) {
  return (
    <span
      style={
        complete
          ? styles.statusPillReady
          : styles.statusPillIncomplete
      }
    >
      <span
        style={
          complete
            ? styles.statusDotReady
            : styles.statusDotIncomplete
        }
      />

      {text || 'Incomplete'}
    </span>
  );
}

const styles = {
  card: {
    padding: '0.85rem 0.95rem',
    marginBottom: '0.8rem',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.03)',
    border:
      '1px solid rgba(120,180,255,0.14)',
  },

  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.8rem',
    flexWrap: 'wrap',
  },

  headingBlock: {
    minWidth: 0,
    flex: 1,
  },

  kicker: {
    color: '#55c7ff',
    fontSize: '0.66rem',
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },

  aircraftLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginTop: '0.25rem',
  },

  registration: {
    color: '#f3f8ff',
    fontSize: '1.02rem',
    fontWeight: 850,
  },

  aircraftType: {
    color: '#8db7e6',
    fontSize: '0.76rem',
    fontWeight: 750,
  },

  bookingLine: {
    color: '#a9c2df',
    fontSize: '0.78rem',
    fontWeight: 700,
    marginTop: '0.25rem',
  },

  badge: {
    display: 'inline-flex',
    padding: '0.12rem 0.42rem',
    borderRadius: 999,
    background: 'rgba(250,204,21,0.10)',
    border:
      '1px solid rgba(250,204,21,0.28)',
    color: '#fff1a6',
    fontSize: '0.58rem',
    fontWeight: 900,
  },

  statusPillReady: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    color: '#8cff8c',
    background: 'rgba(140,255,140,0.08)',
    border:
      '1px solid rgba(140,255,140,0.22)',
    borderRadius: 999,
    padding: '0.25rem 0.55rem',
    fontSize: '0.7rem',
    fontWeight: 850,
    whiteSpace: 'nowrap',
  },

  statusPillIncomplete: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    color: '#ffd27a',
    background: 'rgba(255,210,122,0.07)',
    border:
      '1px solid rgba(255,210,122,0.2)',
    borderRadius: 999,
    padding: '0.25rem 0.55rem',
    fontSize: '0.7rem',
    fontWeight: 850,
    whiteSpace: 'nowrap',
  },

  statusDotReady: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: '#8cff8c',
  },

  statusDotIncomplete: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: '#ffd27a',
  },

  returnLegRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55rem',
    marginTop: '0.7rem',
  },

  returnLegLabel: {
    color: '#8db7e6',
    fontSize: '0.7rem',
    fontWeight: 800,
    textTransform: 'uppercase',
  },

  returnLegPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.18rem 0.5rem',
    borderRadius: 999,
    fontSize: '0.68rem',
    fontWeight: 850,
  },

  warningBox: {
    marginTop: '0.65rem',
    padding: '0.55rem 0.65rem',
    borderRadius: 9,
    background: 'rgba(198,91,91,0.10)',
    border:
      '1px solid rgba(198,91,91,0.32)',
    color: '#ffd0d0',
    fontSize: '0.78rem',
    lineHeight: 1.4,
  },

  infoBox: {
    marginTop: '0.65rem',
    padding: '0.55rem 0.65rem',
    borderRadius: 9,
    background: 'rgba(30,207,255,0.05)',
    border:
      '1px solid rgba(30,207,255,0.18)',
    color: '#b6eaff',
    fontSize: '0.78rem',
    lineHeight: 1.4,
  },

  actions: {
    marginTop: '0.65rem',
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },

  actionButton: {
    padding: '0.38rem 0.65rem',
    fontSize: '0.72rem',
  },

  saving: {
    color: '#b6eaff',
    fontSize: '0.72rem',
  },

  success: {
    color: '#8cff8c',
    fontSize: '0.72rem',
  },

  error: {
    color: '#ff9c9c',
    fontSize: '0.72rem',
  },
};